import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import clsx from "clsx";
import styles from "./SectionScroller.module.scss";

export interface ScrollerSection {
  /** Stable key AND the value reported to `onSelect` / accepted by `scrollTo`. */
  n: number;
  label: string;
}

export interface SectionScrollerProps {
  sections: readonly ScrollerSection[];
  /** Render one section's body. Called once per section — they are ALL mounted. */
  children: (section: ScrollerSection) => ReactNode;
  /**
   * Scroll this section to the top of the pane. Change it to navigate (a `?step` deep-link, a "go fix
   * Basics" link); it is a COMMAND, not the highlight — the rail highlights whatever you are reading.
   */
  scrollTo?: number;
  /** Fired when a rail item is clicked, so the caller can mirror it into the URL. */
  onSelect?: (n: number) => void;
  /** Accessible name for the rail's `<nav>`. */
  railLabel: string;
  /** Optional note under the rail's list. */
  railHint?: ReactNode;
  /** Prefix for each section's DOM id — `${anchorPrefix}-${n}`. Tests and deep-links address these. */
  anchorPrefix?: string;
  className?: string;
}

/**
 * `SectionScroller` — a long form as ONE scroll with a sticky rail that tracks where you are.
 *
 * Every section is mounted at once, so the document reads as a whole and the rail is a table of contents
 * rather than a wizard's step list. Extracted 2026-08-23 from the show editor, which had been a `?step`
 * switcher mounting a single section at a time; the mechanics it owns are the ones that are easy to get
 * subtly wrong and invisible when they are:
 *
 *  • **the scroll-spy** — the active item is the last section whose top has passed the pane's edge, read on
 *    scroll rather than through an IntersectionObserver, so a short section and a tall one both on screen
 *    give an unambiguous answer;
 *  • **anchor scrolling that degrades** — `scrollTo` is not universal (jsdom has no scroll implementation at
 *    all), and an unguarded call throws during mount and takes the surface down with it. Assigning
 *    `scrollTop` is the floor; the smooth version is the enhancement;
 *  • **`scroll-margin-block-start`** on each section, so an anchor jump lands with the heading clear of the
 *    pane's top edge instead of flush against it.
 *
 * It deliberately does NOT own saving. Sections commit however their feature decides — the editor fans out
 * over a registry from one footer button — because "how does this document save" is a product decision, not
 * a layout one.
 */
export function SectionScroller({
  sections,
  children,
  scrollTo,
  onSelect,
  railLabel,
  railHint,
  anchorPrefix = "scroller-sec",
  className,
}: SectionScrollerProps) {
  const paneRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<number>(scrollTo ?? sections[0]?.n ?? 0);
  const anchorId = useCallback((n: number) => `${anchorPrefix}-${n}`, [anchorPrefix]);

  const scrollToSection = useCallback(
    (n: number) => {
      const pane = paneRef.current;
      const el = pane?.querySelector<HTMLElement>(`#${anchorId(n)}`);
      if (!pane || !el) return;
      const top = el.getBoundingClientRect().top - pane.getBoundingClientRect().top + pane.scrollTop;
      if (typeof pane.scrollTo === "function") pane.scrollTo({ top: top - 12, behavior: "smooth" });
      else pane.scrollTop = top - 12;
    },
    [anchorId],
  );

  useEffect(() => {
    const pane = paneRef.current;
    if (!pane) return;
    function repaint() {
      const paneTop = pane!.getBoundingClientRect().top;
      let current = sections[0]?.n ?? 0;
      for (const s of sections) {
        const el = pane!.querySelector<HTMLElement>(`#${anchorId(s.n)}`);
        if (el && el.getBoundingClientRect().top - paneTop <= 24) current = s.n;
      }
      setActive(current);
    }
    pane.addEventListener("scroll", repaint, { passive: true });
    repaint();
    return () => pane.removeEventListener("scroll", repaint);
  }, [sections, anchorId]);

  // A deep-link (or any external navigation) scrolls the pane. Deferred a tick so the sections have laid
  // out — on the first paint the anchor's offset is still 0.
  useEffect(() => {
    if (scrollTo == null) return;
    const id = window.setTimeout(() => scrollToSection(scrollTo), 0);
    return () => window.clearTimeout(id);
  }, [scrollTo, scrollToSection]);

  return (
    <div className={clsx(styles.body, className)}>
      <nav className={styles.rail} aria-label={railLabel}>
        <ol>
          {sections.map((s) => (
            <li key={s.n}>
              <button
                type="button"
                className={styles.railItem}
                aria-current={s.n === active ? "step" : undefined}
                data-active={s.n === active || undefined}
                onClick={() => {
                  onSelect?.(s.n);
                  scrollToSection(s.n);
                }}
              >
                <span className={styles.railNum}>{String(s.n).padStart(2, "0")}</span>
                <span className={styles.railLabel}>{s.label}</span>
              </button>
            </li>
          ))}
        </ol>
        {railHint && <p className={styles.railHint}>{railHint}</p>}
      </nav>

      <main className={styles.pane} ref={paneRef}>
        {sections.map((s) => (
          <section key={s.n} id={anchorId(s.n)} className={styles.section}>
            {children(s)}
          </section>
        ))}
      </main>
    </div>
  );
}
